import {useCallback, useMemo, useState} from 'react';
import Toast from 'react-native-toast-message';

import type {SavedCard} from '../../../components/payments/till/PaymentMethodSelector/types';
import {ERROR_MESSAGE, SUCCESS_CHECK} from '../../../components/ToastBottom';
import {
  PAYMENT_SERVICE_TYPES,
  type PaymentCard,
  useDeletePaymentCard,
  usePaymentCard,
  usePaymentServices,
} from '../../../queries/paymentCard';

export interface CardManagementResult {
  /** Derived saved card from payment card query (memoized) */
  savedCard: SavedCard | undefined;
  /** Raw payment card from API (for validation hooks) */
  paymentCard: PaymentCard | null | undefined;
  /** Whether payment card or services is loading */
  isLoading: boolean;
  /** Whether a saved-card delete is in progress (drives the card-row CHANGE/DELETE spinner) */
  isCardActionLoading: boolean;
  /** Open card tokeniser modal */
  handleAddOrChangeCard: () => void;
  /** Delete saved card */
  handleDeleteCard: () => void;
  /** Card tokeniser modal visibility */
  isCardTokeniserModalVisible: boolean;
  setCardTokeniserModalVisible: (visible: boolean) => void;
  /** No payment service modal visibility */
  isNoPaymentServiceModalVisible: boolean;
  setNoPaymentServiceModalVisible: (visible: boolean) => void;
}

/**
 * Hook for managing card payment state and handlers.
 * Consolidates card management logic from PaymentContent and CheckoutPaymentScreen.
 *
 * Uses TanStack Query hooks for both card data and payment services.
 * No longer depends on paymentStore - uses usePaymentServices() directly.
 */
export function useCardManagement(): CardManagementResult {
  const [isCardTokeniserModalVisible, setCardTokeniserModalVisible] = useState(false);
  const [isNoPaymentServiceModalVisible, setNoPaymentServiceModalVisible] = useState(false);

  // Fetch card and services from server via TanStack Query
  const {data: paymentCardData, isLoading: isLoadingCard} = usePaymentCard();
  const {data: paymentServicesData, isLoading: isLoadingServices} = usePaymentServices();
  const deletePaymentCard = useDeletePaymentCard();

  // Derive Till service availability from query (same logic as PaymentMethodLoyaltyEnabled)
  const tillPaymentService = useMemo(
    () =>
      paymentServicesData?.services?.find(s => s.type === PAYMENT_SERVICE_TYPES.TILL_PAYMENTS) ??
      null,
    [paymentServicesData?.services],
  );

  // Derive savedCard from query response (memoized to avoid new object every render)
  const paymentCard = paymentCardData?.card;
  const savedCard = useMemo<SavedCard | undefined>(
    () =>
      paymentCard
        ? {
            cardType: paymentCard.card_type,
            lastFourDigits: paymentCard.obfuscated_card?.slice(-4) ?? '',
            expiryMonth: paymentCard.expiry_month,
            expiryYear: paymentCard.expiry_year,
          }
        : undefined,
    [paymentCard],
  );

  const handleAddOrChangeCard = useCallback(() => {
    if (tillPaymentService) {
      setCardTokeniserModalVisible(true);
    } else {
      setNoPaymentServiceModalVisible(true);
    }
  }, [tillPaymentService]);

  const handleDeleteCard = useCallback(() => {
    deletePaymentCard.mutate(undefined, {
      onSuccess: () => {
        Toast.show({type: SUCCESS_CHECK});
      },
      onError: (error: Error) => {
        Toast.show({
          type: ERROR_MESSAGE,
          text1: error.message || 'Failed to delete card',
        });
      },
    });
  }, [deletePaymentCard]);

  return {
    savedCard,
    paymentCard,
    isLoading: isLoadingCard || isLoadingServices || deletePaymentCard.isPending,
    isCardActionLoading: deletePaymentCard.isPending,
    handleAddOrChangeCard,
    handleDeleteCard,
    isCardTokeniserModalVisible,
    setCardTokeniserModalVisible,
    isNoPaymentServiceModalVisible,
    setNoPaymentServiceModalVisible,
  };
}
